import "../styles/rediscover.css";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { Card, CardFields, Doc, Tag } from "../../../shared/types.ts";
import { normalizeTag } from "../../../shared/normalize.ts";
import { autoFields, cardColors } from "./detail/rules.ts";
import { CoverButton } from "./detail/Cover.tsx";
import { Citations, coarse, Hint, holdKeyboard, PrivateToggle, Recognized } from "./detail/Extras.tsx";
import { ActionChip, DoneChip, KindChip } from "./detail/Facts.tsx";
import { IconBook, IconExpand, IconSpark } from "./detail/icons.tsx";
import { SimilarButton } from "./detail/Similar.tsx";
import { Summary } from "./detail/Summary.tsx";
import { Viewer } from "./detail/Viewer.tsx";
import { hasMedia, Media, shownAs } from "./detail/Media.tsx";
import { Favicon, MOD } from "./Grid.tsx";
import { IconChevronDown, IconClose, IconCopy, IconDownload, IconExternal, IconRestore, IconTrash } from "./Icons.tsx";
import { absoluteTime, cardImage, contrast, quietIngest, relativeTime, safeColor, TEXT_KINDS } from "./lib.ts";
import { PinButton } from "./pins/PinsStrip.tsx";
import { Reader } from "./reader/Reader.tsx";
import { isWriterKey, Writer } from "./reader/Writer.tsx";
import { popPlace, RichText } from "./RichText.tsx";
import { ShareButton } from "./share/ShareButton.tsx";
import { AddToSpaceButton } from "./spaces/AddToSpace.tsx";
import { byId, closeCard, deleteCard, dismiss, failed, refocus, route, searchFor, showCard, showModal, store, toast, toasts, topTags, visible, waitsForAi, width } from "./state.ts";

/**
 * ←/→: anda na lista que está na tela (Tudo sem privados, ou a busca). Card fora dela (lixeira, Parecidos, ligação a
 * outro card) fica parado; no Reencontro também: a rodada decide a ordem.
 */
export function stepCard(dir: 1 | -1) {
  if (route.value.path === "/reencontro" || document.querySelector("dialog[open]:not(.detail), :popover-open")) return; // leitura, escrita, Parecidos ou menu por cima
  const list = visible.value;
  const i = list.findIndex((c) => c.id === route.value.cardId);
  const next = i < 0 ? undefined : list[i + dir];
  if (next) showCard(next.id);
}

const copy = (value: string, done: string) =>
  navigator.clipboard?.writeText(value).then(
    () => toast({ key: `copy:${value}`, text: done }),
    () => toast({ key: "copy-fail", text: "Não deu para copiar." }),
  );

/**
 * Detalhe por cima da grade: <dialog> modal (prende o foco), aberto pelo hash #<cardId>; a rolagem da grade fica.
 * Fechar volta no histórico (closeCard): o gesto de voltar do iOS e o botão do Android fazem o mesmo que o ×.
 */
export function Detail() {
  const id = route.value.cardId;
  const card = id ? byId.value.get(id) : undefined;
  const ref = useRef<HTMLDialogElement>(null);
  const back = useRef<HTMLElement | null>(null);
  const last = useRef<string | null>(null);
  const downOnBackdrop = useRef(false);
  if (card) last.current = card.id;
  // Cor dominante no diálogo inteiro: fundo da mídia, sombra do painel e, ≤ 1024, a barra do topo (título e fechar).
  const dom = card && card.kind !== "note" ? safeColor(card.dominantColor) : undefined;
  const onDom = dom && card?.kind === "image" ? `;--on-dom:${contrast(dom, "#ffffff") > contrast(dom, "#000000") ? "#fff" : "#000"}` : "";

  useEffect(() => {
    const d = ref.current!;
    if (card && !d.open) {
      back.current = document.activeElement as HTMLElement | null;
      showModal(d);
      // Toque: o foco fica no próprio diálogo (o leitor de tela anuncia o detalhe), sem o anel no × a cada abertura.
      if (coarse()) d.focus();
    } else if (!card && d.open) d.close();
  }, [!!card]);

  // Foco volta ao card visto por último (as setas podem ter trocado): o link que abriu, se for ele; senão o que está à
  // vista, sem rolar; senão o da grade. O mesmo card pode estar em Em foco e na grade: focar o da faixa, lá no alto,
  // levava a página ao topo (no iPhone o toque não foca o link, então quem abriu nem sempre é pista).
  const restoreFocus = () => {
    const all = [...document.querySelectorAll<HTMLElement>(`a[href="#${CSS.escape(encodeURIComponent(last.current ?? ""))}"]`)];
    const seen = all.find((a) => {
      const r = a.getBoundingClientRect();
      return r.height > 0 && r.bottom > 0 && r.top < innerHeight;
    });
    const to = (back.current && all.includes(back.current) ? back.current : null) ?? seen ?? all.find((a) => a.closest(".grid")) ?? all[0] ?? back.current;
    to?.focus({ preventScroll: to === seen });
  };

  return (
    <dialog
      ref={ref}
      class="detail"
      aria-label="Detalhe do card"
      tabIndex={-1}
      style={dom ? `--dom:${dom}${onDom}` : undefined}
      data-dom={dom ? "" : undefined}
      onCancel={(e) => {
        e.preventDefault();
        closeCard();
      }}
      onClose={restoreFocus}
      onPointerDown={(e) => (downOnBackdrop.current = e.target === e.currentTarget)}
      onClick={(e) => downOnBackdrop.current && e.target === e.currentTarget && closeCard()}
    >
      {/* Faixa de fechar (só mouse; o teclado tem o × e o Esc). */}
      <div class="detail-strip" aria-hidden="true" onClick={() => closeCard()} />
      <button type="button" class="detail-close" aria-label="Fechar" title="Fechar (Esc)" autofocus={!coarse()} onClick={() => closeCard()}>
        {width.value > 1024 ? <IconClose /> : <IconChevronDown />}
      </button>
      {card && <DetailBody key={card.id} card={card} />}
    </dialog>
  );
}

function DetailBody({ card: c }: { card: Card }) {
  const update = (f: Partial<CardFields>) => store.update(c.id, f).catch(failed);
  const src = c.sourceUrl && /^https?:\/\//i.test(c.sourceUrl) ? c.sourceUrl : null;
  const v = shownAs(c); // Vídeo, PDF, Áudio sem o arquivo: mostra o link ou o texto do card (gap-capture)
  const shown = cardImage(v, store.previews.value, 1280);
  const text = TEXT_KINDS.has(v.kind);
  const [writing, setWriting] = useState(false);
  const [reading, setReading] = useState(false);
  const [edition, setEdition] = useState(0); // remonta o editor da nota depois do modo escrita
  // Título digitado e ainda não salvo. Salva no blur e também ao desmontar: Esc e Voltar fecham sem blur.
  const typedTitle = useRef<string | null>(null);
  const saveTitle = () => {
    if (typedTitle.current === null) return;
    const v = typedTitle.current.trim() || null;
    typedTitle.current = null;
    if (v !== store.get(c.id)?.title) update({ title: v });
  };
  useEffect(() => saveTitle, []);
  // Trocou de card por um link de dentro (Parecidos, ligação): o foco não fica perdido no body.
  useEffect(() => refocus(() => document.querySelector<HTMLElement>("dialog.detail .detail-close")), []);
  // "Link copiado.", "Cor … copiada." falam deste card: saem com ele (fechar ou trocar), não seguem para o próximo.
  useEffect(() => () => toasts.peek().forEach((t) => t.key.startsWith("copy:") && dismiss(t.key)), []);

  // Texto da nota: checklist marca tarefa, aspas viram citação, códigos #rrggbb viram cor (noteFields; origem automática).
  const saveText = (content: Doc, contentText: string) => update({ content, contentText, ...autoFields(store.get(c.id) ?? c, content, contentText) });
  const openWriter = () => {
    // O editor do detalhe salva no blur (síncrono): o modo escrita abre com o texto mais novo.
    const el = document.activeElement as HTMLElement | null;
    if (el?.closest(".guts")) el.blur();
    holdKeyboard(); // iPhone: o teclado sai deste toque e o editor do modo escrita o herda
    setWriting(true);
  };
  useEffect(() => {
    if (!text) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !isWriterKey(e) || document.querySelector("dialog[open]:not(.detail)")) return;
      e.preventDefault();
      openWriter();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [text]);

  return (
    <div class="detail-box">
      <div class={`guts is-${v.kind}${text && v.kind !== "note" ? " is-note" : ""}${shown ? " has-media" : ""}`}>
        <div class="guts-media">
          <DetailMedia key={edition} card={v} src={shown} link={src} onText={saveText} onRead={() => setReading(true)} />
        </div>
        {text && (
          <button type="button" class="round rd-write-btn" aria-label="Modo escrita" data-tip={`Modo escrita (${MOD}+Shift+F)`} onClick={openWriter}>
            <IconExpand />
          </button>
        )}
        {shown && <MediaFoot card={c} />}
      </div>
      <section class="panel" aria-label="Sobre este card">
        <header class="panel-head">
          <input
            class="title-input"
            aria-label="Título"
            placeholder={c.kind === "highlight" ? "Destaque" : c.kind === "quote" ? "Citação" : "Sem título"}
            defaultValue={c.title ?? ""}
            onInput={(e) => (typedTitle.current = e.currentTarget.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          />
          <p class="meta">
            <time dateTime={new Date(c.createdAt).toISOString()} title={absoluteTime(c.createdAt)}>
              {relativeTime(c.createdAt)}
            </time>
          </p>
        </header>
        <div class="panel-body">
          <Summary card={c} />
          <Tags card={c} />
          <section class="annot" aria-labelledby="note-label">
            <div class="section-head">
              <h3 class="section-label" id="note-label">
                Anotação
              </h3>
              <Hint text="Um lembrete seu sobre este card." />
            </div>
            <div class="note-box">
              <RichText doc={c.note} label="Anotação" placeholder="Algo para lembrar sobre isto…" links cardId={c.id} onSave={(note, noteText) => update({ note, noteText: noteText || null })} />
            </div>
          </section>
          <Citations card={c} />
          <Recognized card={c} />
          {!c.deletedAt && <PrivateToggle card={c} />}
        </div>
        <Actions card={c} link={src} />
      </section>
      {writing && (
        <Writer
          card={c}
          onSave={saveText}
          onClose={() => {
            setWriting(false);
            setEdition((n) => n + 1);
            refocus(() => document.querySelector<HTMLElement>("dialog.detail .rd-write-btn"));
          }}
        />
      )}
      {reading && <Reader card={c} onClose={() => setReading(false)} />}
    </div>
  );
}

/**
 * Capa trocável: só o link, o único que mostra a capa (grade e detalhe). ponytail: PDF e vídeo entram quando a face e o
 * detalhe deles (Media.tsx, gap-capture) desenharem o coverBlobId; antes disso a troca não aparecia em lugar nenhum.
 */
const COVERED = new Set<Card["kind"]>(["link"]);

/**
 * No celular, "Baixar original" vai para a folha de compartilhar do sistema (Salvar imagem, Fotos): num app da tela de
 * início o download abriria a imagem crua na janela, sem como voltar. Sem a folha (ou se ela falhar), baixa como antes.
 * ponytail: conferir no iPhone; a folha exige o toque recente, e o fetch do original pode demorar.
 */
async function saveOriginal(e: MouseEvent, href: string, name: string) {
  if (!coarse() || !navigator.canShare) return;
  e.preventDefault();
  try {
    const blob = await (await fetch(href)).blob();
    const file = new File([blob], `${name}.${blob.type.split("/")[1] ?? "jpg"}`, { type: blob.type });
    if (!navigator.canShare({ files: [file] })) throw new Error("share");
    await navigator.share({ files: [file] });
  } catch (err) {
    if ((err as Error).name === "AbortError") return; // a pessoa fechou a folha
    const a = Object.assign(document.createElement("a"), { href, download: name });
    a.click();
  }
}

function Actions({ card: c, link }: { card: Card; link: string | null }) {
  const blob = c.kind === "image" ? c.primaryBlobId : null;
  const here = `${location.origin}/#${encodeURIComponent(c.id)}`;
  // Origem sempre que houver source_url (imagem da extensão também), ao lado de baixar ou copiar.
  return (
    <div class="panel-actions">
      {blob && (
        <a
          class="round"
          href={`/media/${encodeURIComponent(blob)}`}
          download={c.title ?? ""}
          aria-label="Baixar original"
          data-tip="Baixar original"
          onClick={(e) => void saveOriginal(e, `/media/${encodeURIComponent(blob)}`, (c.title ?? "imagem").replace(/\.[a-z0-9]{2,5}$/i, ""))}
        >
          <IconDownload />
        </a>
      )}
      {link && (
        <a class="round" href={link} target="_blank" rel="noopener noreferrer" aria-label="Abrir origem (abre em outra aba)" data-tip="Abrir origem">
          <IconExternal />
        </a>
      )}
      {TEXT_KINDS.has(c.kind) && (
        <button type="button" class="round" aria-label="Copiar texto" data-tip="Copiar texto" onClick={() => copy(store.get(c.id)?.contentText ?? "", "Texto copiado.")}>
          <IconCopy />
        </button>
      )}
      {!c.deletedAt && (
        <>
          {COVERED.has(c.kind) && <CoverButton card={c} />}
          <AddToSpaceButton card={c} />
          <PinButton card={c} />
          <ShareButton card={c} />
        </>
      )}
      <button type="button" class="round" aria-label="Copiar link" data-tip="Copiar link" onClick={() => copy(link ?? here, "Link copiado.")}>
        <IconCopy />
      </button>
      {c.deletedAt ? (
        <button type="button" class="round" aria-label="Restaurar" data-tip="Restaurar" onClick={() => store.restore(c.id).catch(failed)}>
          <IconRestore />
        </button>
      ) : (
        <button
          type="button"
          class="round"
          aria-label="Apagar"
          data-tip="Apagar"
          onClick={() => {
            closeCard();
            deleteCard(c.id).catch(failed);
          }}
        >
          <IconTrash />
        </button>
      )}
    </div>
  );
}

/** Paleta (toque copia a cor; em leque no hover, aberta no toque) e "Parecidos" (só imagem, só com a análise da IA). */
function MediaFoot({ card: c }: { card: Card }) {
  const dom = safeColor(c.dominantColor);
  const rest = Object.entries(c.palette ?? {})
    .filter(([hex]) => safeColor(hex) && hex.toLowerCase() !== dom?.toLowerCase())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([hex]) => hex);
  const swatches = dom ? [...rest, dom] : rest;
  const dark = dom ? contrast(dom, "#ffffff") > contrast(dom, "#000000") : false;
  return (
    <div class="media-foot">
      {swatches.length > 0 && (
        <ul class="palette" aria-label="Cores">
          {swatches.map((hex) => (
            <li key={hex}>
              <button type="button" class="sw" style={`--c:${hex}`} aria-label={`Copiar cor ${hex}`} data-tip={hex} onClick={() => copy(hex, `Cor ${hex} copiada.`)} />
            </li>
          ))}
        </ul>
      )}
      {/* Sem a chave da IA o vetor da imagem é só o nome: Parecidos não teria o que comparar (09-PARIDADE §6.1). */}
      {c.kind === "image" && !c.deletedAt && !quietIngest(c) && <SimilarButton card={c} onDark={dark} />}
    </div>
  );
}

/** Imagem do detalhe: tocar abre em tela cheia, com pinça e zoom (Viewer). */
function ZoomImage({ card: c, src }: { card: Card; src: string }) {
  const [open, setOpen] = useState(false);
  const alt = c.title ?? "Imagem guardada";
  return (
    <>
      <button type="button" class="rd-zoom" aria-label={`Ver em tela cheia: ${alt}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <img src={src} alt={alt} />
      </button>
      {open && <Viewer src={src} full={c.primaryBlobId ? `/media/${encodeURIComponent(c.primaryBlobId)}` : src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

type MediaProps = { card: Card; src: string | null; link: string | null; onText: (doc: Doc, text: string) => void; onRead: () => void };
function DetailMedia({ card: c, src, link, onText, onRead }: MediaProps) {
  if (hasMedia(c)) return <Media card={c} link={link} />; // PDF, vídeo, áudio e player do YouTube/Vimeo (gap-capture)
  if (c.kind === "image") {
    return src ? <ZoomImage card={c} src={src} /> : <p class="excerpt">{c.title ?? "Imagem ainda sem arquivo."}</p>;
  }
  if (c.kind === "link") {
    return (
      <div class="link-view">
        {src && <img src={src} alt="" />}
        <h2>{c.title ?? c.sourceUrl}</h2>
        <p class="domain">
          <Favicon domain={c.sourceDomain} />
          {c.sourceDomain}
        </p>
        <div class="rd-link-actions">
          {c.readerAvailable && (
            <button type="button" class="rd-read-btn" onClick={onRead}>
              <IconBook />
              Modo leitura
            </button>
          )}
          {link && (
            <a class="open-site" href={link} target="_blank" rel="noopener noreferrer">
              Abrir no site<span class="sr-only"> (abre em outra aba)</span>
            </a>
          )}
        </div>
      </div>
    );
  }
  if (TEXT_KINDS.has(c.kind)) {
    const origin = c.kind !== "note" && c.sourceDomain;
    const colors = c.kind === "color" ? cardColors(c) : [];
    return (
      <>
        {c.kind === "color" ? (
          colors.length > 0 && <ul class="rd-colors" aria-label={colors.length > 1 ? "Paleta" : "Cor"}>
            {colors.map((h) => (
              <li key={h}>
                <button type="button" class="rd-color" style={`--c:${h};--on:${contrast(h, "#ffffff") > contrast(h, "#000000") ? "#fff" : "#000"}`} aria-label={`Copiar cor ${h}`} onClick={() => copy(h, `Cor ${h} copiada.`)}>
                  <span aria-hidden="true">{h}</span>
                  <IconCopy />
                </button>
              </li>
            ))}
          </ul>
        ) : c.kind !== "note" && (
          <p class="rd-text-kind">
            {c.kind === "quote" ? "Citação" : "Destaque"}
            {origin && link && (
              <>
                {" · trecho de "}
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {origin.replace(/^www\./, "")}
                  <span class="sr-only"> (abre em outra aba)</span>
                </a>
              </>
            )}
          </p>
        )}
        <RichText doc={c.content} label="Texto da nota" placeholder="Nota vazia" links slash cardId={c.id} onSave={onText} />
      </>
    );
  }
  return <p class="excerpt">{c.contentText ?? c.title}</p>;
}

function Tags({ card: c }: { card: Card }) {
  const all = store.tags.value;
  const mine = c.tagIds.map((id) => all.get(id)).filter((t): t is Tag => !!t);
  const suggestions = topTags.value.filter((t) => !c.tagIds.includes(t.id)).slice(0, 30);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const list = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const addBtn = useRef<HTMLButtonElement>(null);
  // Mais de 3 linhas: corta e oferece "mostrar todas".
  // Medido também quando o diálogo abre (antes dele a lista não tem tamanho): ResizeObserver.
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const check = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mine.length, expanded, adding]);
  // Sugestões (§4.1): digitar abre a lista das mais usadas que combinam; "#" sozinho mostra as mais usadas.
  const [typed, setTyped] = useState("");
  const [pick, setPick] = useState(-1); // nenhuma marcada: Enter guarda o que foi digitado; ↓ marca uma sugestão
  const [listHidden, setListHidden] = useState(false); // Esc fecha só a lista; digitar abre de novo
  const [, setViewport] = useState(0); // teclado na tela abriu ou fechou: a lista se reposiciona
  const listId = useId();
  const q = normalizeTag(typed.replace(/^#+/, ""));
  const options = adding && typed.trim() && !listHidden ? suggestions.filter((t) => t.normalized.includes(q)).slice(0, 8) : [];
  const at = options.length ? input.current?.getBoundingClientRect() : undefined;
  useEffect(() => {
    const vv = window.visualViewport;
    if (!options.length || !vv) return;
    const again = () => setViewport((n) => n + 1);
    vv.addEventListener("resize", again);
    vv.addEventListener("scroll", again);
    return () => {
      vv.removeEventListener("resize", again);
      vv.removeEventListener("scroll", again);
    };
  }, [options.length > 0]);
  const close = () => {
    setAdding(false);
    setTyped("");
    requestAnimationFrame(() => addBtn.current?.focus());
  };
  const attach = (raw: string) => {
    const name = raw.replace(/^#+/, "").trim();
    if (name) store.attachTag(c.id, name).catch(failed);
    if (input.current) input.current.value = "";
    setTyped("");
    setPick(-1);
  };
  const add = (e: Event) => {
    e.preventDefault();
    attach(options[pick]?.name ?? input.current!.value);
  };
  // Tirar uma etiqueta da IA é definitivo para este card (a reanálise não recria). Não há "dessuprimir": devolver
  // anexa de novo como etiqueta da pessoa, e o aviso diz isso. "mixed" = a origem deste vínculo não vem no contrato.
  const detach = (t: Tag) => {
    store.detachTag(c.id, t.id).then(() => {
      if (t.origin === "user") return;
      const why = t.origin === "ai" ? "A IA não volta a sugerir para este card." : "Se veio da IA, ela não volta a sugerir para este card.";
      toast({ key: `untag:${c.id}:${t.id}`, text: `Etiqueta “${t.name}” tirada. ${why}`, action: { label: "Devolver como sua", run: () => void store.attachTag(c.id, t.name).catch(failed) } });
    }, failed);
  };
  return (
    <section class="tags" aria-labelledby="tags-label">
      <div class="section-head">
        <h3 class="section-label" id="tags-label">
          Etiquetas
        </h3>
        {/* Sem a chave da IA não há etiqueta da IA: a dica não fala do ✦. */}
        <Hint text={waitsForAi(c) ? "Etiquetas ajudam a achar de novo." : "Etiquetas ajudam a achar de novo. ✦ marca as que vieram (ou podem ter vindo) da IA."} />
      </div>
      {/* Tipo, ação e feito numa fileira própria: o recorte das etiquetas nunca esmaece um botão. */}
      {!c.deletedAt && (
        <div class="rd-facts">
          <KindChip card={c} />
          <ActionChip card={c} />
          {c.action && <DoneChip card={c} />}
        </div>
      )}
      <ul class={`tag-list${expanded ? "" : " clip"}${clipped ? " over" : ""}`} ref={list}>
        <li class="rd-add">
          {!adding && (
            <button
              type="button"
              class="add-tag"
              ref={addBtn}
              onClick={() => {
                // Foco no campo dentro do toque (o iPhone só abre o teclado assim); o campo já está montado, escondido.
                input.current?.focus();
                setAdding(true);
              }}
            >
              + etiqueta
            </button>
          )}
          <form onSubmit={add} class={adding ? undefined : "rd-idle"}>
            <input
              ref={input}
              name="tag"
              class="tag-input"
              role="combobox"
              tabIndex={adding ? undefined : -1}
              aria-hidden={adding ? undefined : "true"}
              aria-expanded={options.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={options[pick] ? `${listId}-${pick}` : undefined}
              placeholder="nova etiqueta"
              aria-label="Nova etiqueta"
              autocomplete="off"
              autocapitalize="none"
              enterkeyhint="done"
              onFocus={() => setAdding(true)}
              onInput={(e) => {
                setTyped(e.currentTarget.value);
                setPick(-1);
                setListHidden(false);
              }}
              onKeyDown={(e) => {
                const n = options.length;
                if ((e.key === "ArrowDown" || e.key === "ArrowUp") && n) {
                  e.preventDefault();
                  setPick(e.key === "ArrowDown" ? (pick + 1) % n : pick <= 0 ? n - 1 : pick - 1);
                  return;
                }
                if (e.key !== "Escape") return;
                e.preventDefault(); // Esc fecha a lista, depois o campo; nunca o detalhe
                if (n) setListHidden(true);
                else close();
              }}
              onBlur={(e) => {
                setListHidden(true);
                if (!e.currentTarget.value.trim()) setAdding(false);
              }}
            />
          </form>
        </li>
        {mine.map((t) => {
          // Tag.origin é da conta inteira: "mixed" (mão num card, IA em outro) pode ser IA aqui também.
          const ai = t.origin === "ai" ? "sugerida pela IA" : t.origin === "mixed" ? "pode ter vindo da IA" : null;
          return (
            <li class={`tag${ai ? " is-ai" : ""}`} key={t.id}>
              <button
                type="button"
                class="tag-name"
                title={ai ? `${ai.charAt(0).toUpperCase()}${ai.slice(1)}. Buscar esta etiqueta` : "Buscar esta etiqueta"}
                onClick={() => closeCard(() => searchFor(`#${t.name}`))}
              >
                {ai && <IconSpark />}
                {t.name}
                {ai && <span class="sr-only"> ({ai})</span>}
              </button>
              <button type="button" class="tag-x" aria-label={`Tirar a etiqueta ${t.name}`} onClick={() => detach(t)}>
                <IconClose />
              </button>
            </li>
          );
        })}
      </ul>
      {clipped && !expanded && (
        <button type="button" class="text-btn more" onClick={() => setExpanded(true)}>
          mostrar todas
        </button>
      )}
      {options.length > 0 && (
        <ul class="suggest tag-suggest" id={listId} role="listbox" aria-label="Etiquetas mais usadas" style={at ? `left:${Math.round(at.left)}px;${popPlace(at)}` : undefined}>
          {options.map((t, i) => (
            <li
              key={t.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === pick}
              class="suggest-item"
              onMouseDown={(e) => {
                e.preventDefault(); // o foco fica no campo
                attach(t.name);
              }}
              onMouseMove={() => i !== pick && setPick(i)}
            >
              <span class="suggest-label">#{t.name}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
